import React, { useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Line } from 'recharts';
import axios from 'axios';
import { useQuery } from 'react-query';
import { formatDistanceToNow } from 'date-fns';
import { api } from '../utils/api';

import LoadingSpinner from '../components/LoadingSpinner';
import MalawiDistrictsMap from '../components/MalawiDistrictsMap';
import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  ChatBubbleLeftRightIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

/**
 * IMPORTANT
 * - This page never forces a full reload. Data auto-refreshes via react-query.
 * - A sticky "Last updated" badge shows when data last changed.
 * - Everything is in ONE file as requested (no external UI subcomponents).
 */

// ————————————————————————————————————————————————————————————————
// Tiny in-file components
// ————————————————————————————————————————————————————————————————

// Animated Counter
const AnimatedCounter: React.FC<{ totalIncidents: number }> = ({ totalIncidents }) => {
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    const duration = 2000; // 2 seconds
    const increment = totalIncidents / (duration / 16); // ~60fps
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= totalIncidents) {
        setCount(totalIncidents);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [totalIncidents]);

  return (
    <span className="text-2xl font-bold text-white tabular-nums drop-shadow-lg">
      {count.toLocaleString()}
    </span>
  );
};

// Last Updated badge
const LastUpdated: React.FC<{ updatedAt?: Date | null }> = ({ updatedAt }) => {
  return (
    <div className="flex items-center gap-2 text-xs text-gray-500">
      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden />
      <span className="font-medium">Last updated:</span>
      <span>
        {updatedAt
          ? `${formatDistanceToNow(updatedAt, { addSuffix: true })} • ${updatedAt.toLocaleTimeString()}`
          : 'waiting…'}
      </span>
    </div>
  );
};

// Location-specific incident view component
const LocationIncidentsView: React.FC<{
  locationData: any;
  selectedLocation: { name: string, type: string };
  loading: boolean;
  onClose: () => void;
}> = ({ locationData, selectedLocation, loading, onClose }) => {
  if (loading) {
    return (
      <div className="overflow-hidden bg-white border border-gray-200 shadow-xl rounded-2xl">
        <div className="relative px-6 py-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative z-10 flex items-center justify-between">
            <h2 className="flex items-center text-xl font-bold text-white">
              <div className="p-2 mr-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              Loading {selectedLocation.name}...
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-white transition-colors rounded-lg bg-white/20 backdrop-blur-sm hover:bg-white/30"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="flex items-center justify-center p-12">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!locationData) {
    return (
      <div className="overflow-hidden bg-white border border-gray-200 shadow-xl rounded-2xl">
        <div className="relative px-6 py-4 bg-gradient-to-r from-red-500 to-pink-500">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative z-10 flex items-center justify-between">
            <h2 className="flex items-center text-xl font-bold text-white">
              <div className="p-2 mr-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              No data found for {selectedLocation.name}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-white transition-colors rounded-lg bg-white/20 backdrop-blur-sm hover:bg-white/30"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="p-8 text-center">
          <p className="text-gray-600">No incidents found for this location.</p>
        </div>
      </div>
    );
  }

  const { summary, details } = locationData;

  return (
    <div className="overflow-hidden bg-white border border-gray-200 shadow-xl rounded-2xl">
      {/* Header */}
      <div className="relative px-6 py-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 flex items-center justify-between">
          <h2 className="flex items-center text-xl font-bold text-white">
            <div className="p-2 mr-3 bg-white/20 backdrop-blur-sm rounded-xl">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            {selectedLocation.name} - {summary.total_incidents} Incidents
            <div className="flex items-center ml-4 space-x-2">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-white/90">LIVE</span>
            </div>
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-white transition-colors rounded-lg bg-white/20 backdrop-blur-sm hover:bg-white/30"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Summary Statistics */}
          <div className="space-y-6">
            {/* Total Overview */}
            <div className="relative p-6 overflow-hidden transition-all duration-300 border-2 border-blue-200 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 rounded-2xl hover:shadow-xl">
              <div className="absolute top-0 right-0 w-24 h-24 transform translate-x-8 -translate-y-8 bg-blue-500 rounded-full opacity-10"></div>
              <div className="relative z-10 text-center">
                <h3 className="mb-4 text-lg font-bold text-gray-900">Location Overview</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-2xl font-bold text-blue-700">{summary.polling_centers}</div>
                    <div className="text-blue-600">Polling Centers</div>
                  </div>
                  {selectedLocation.type === 'district' && (
                    <div>
                      <div className="text-2xl font-bold text-blue-700">{summary.constituencies}</div>
                      <div className="text-blue-600">Constituencies</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Enhanced Status Breakdown - All statuses except 'investigating' */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900">Status Distribution</h3>
              <div className="grid grid-cols-2 gap-3">
                {/* Reported */}
                <div className="relative p-3 overflow-hidden transition-all duration-300 border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl hover:shadow-lg">
                  <div className="text-center">
                    <div className="mb-1 text-xl font-bold text-amber-700">
                      {summary.status_counts?.reported || 0}
                    </div>
                    <div className="text-xs font-medium text-amber-600">Reported</div>
                  </div>
                </div>

                {/* Resolved */}
                <div className="relative p-3 overflow-hidden transition-all duration-300 border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl hover:shadow-lg">
                  <div className="text-center">
                    <div className="mb-1 text-xl font-bold text-green-700">
                      {summary.status_counts?.resolved || 0}
                    </div>
                    <div className="text-xs font-medium text-green-600">Resolved</div>
                  </div>
                </div>

                {/* Dismissed */}
                <div className="relative p-3 overflow-hidden transition-all duration-300 border-2 border-red-200 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl hover:shadow-lg">
                  <div className="text-center">
                    <div className="mb-1 text-xl font-bold text-red-700">
                      {summary.status_counts?.dismissed || 0}
                    </div>
                    <div className="text-xs font-medium text-red-600">Dismissed</div>
                  </div>
                </div>

                {/* Uncompliant */}
                <div className="relative p-3 overflow-hidden transition-all duration-300 border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl hover:shadow-lg">
                  <div className="text-center">
                    <div className="mb-1 text-xl font-bold text-orange-700">
                      {summary.status_counts?.uncompliant || 0}
                    </div>
                    <div className="text-xs font-medium text-orange-600">Uncompliant</div>
                  </div>
                </div>

                {/* Sent for Investigation */}
                <div className="relative p-3 overflow-hidden transition-all duration-300 border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl hover:shadow-lg">
                  <div className="text-center">
                    <div className="mb-1 text-xl font-bold text-blue-700">
                      {summary.status_counts?.sent_for_investigation || 0}
                    </div>
                    <div className="text-xs font-medium text-blue-600">Sent for Investigation</div>
                  </div>
                </div>

                {/* Sent for Intervention */}
                <div className="relative p-3 overflow-hidden transition-all duration-300 border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl hover:shadow-lg">
                  <div className="text-center">
                    <div className="mb-1 text-xl font-bold text-purple-700">
                      {summary.status_counts?.sent_for_intervention || 0}
                    </div>
                    <div className="text-xs font-medium text-purple-600">Sent for Intervention</div>
                  </div>
                </div>

                {/* Documented */}
                <div className="relative p-3 overflow-hidden transition-all duration-300 border-2 border-teal-200 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl hover:shadow-lg">
                  <div className="text-center">
                    <div className="mb-1 text-xl font-bold text-teal-700">
                      {summary.status_counts?.documented || 0}
                    </div>
                    <div className="text-xs font-medium text-teal-600">Documented</div>
                  </div>
                </div>

                {/* Intervened */}
                <div className="relative p-3 overflow-hidden transition-all duration-300 border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl hover:shadow-lg">
                  <div className="text-center">
                    <div className="mb-1 text-xl font-bold text-emerald-700">
                      {summary.status_counts?.intervened || 0}
                    </div>
                    <div className="text-xs font-medium text-emerald-600">Intervened</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Details and Charts */}
          <div className="space-y-6">
            {/* Severity Chart */}
            <div className="relative p-6 overflow-hidden transition-all duration-300 border-2 border-gray-200 bg-gradient-to-br from-white via-gray-50 to-white rounded-2xl hover:shadow-xl">
              <div className="absolute top-0 right-0 w-24 h-24 transform translate-x-8 -translate-y-8 bg-indigo-500 rounded-full opacity-5"></div>
              <div className="relative z-10">
                <h3 className="mb-6 text-lg font-bold text-center text-gray-900">Severity Distribution</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Critical', value: summary.severity_counts.critical },
                        { name: 'High', value: summary.severity_counts.high },
                        { name: 'Medium', value: summary.severity_counts.medium },
                        { name: 'Low', value: summary.severity_counts.low }
                      ]}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={25}
                      paddingAngle={2}
                    >
                      <Cell fill="#DC2626" /> {/* Critical - red */}
                      <Cell fill="#F59E0B" /> {/* High - amber */}
                      <Cell fill="#3B82F6" /> {/* Medium - blue */}
                      <Cell fill="#6B7280" /> {/* Low - gray */}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      wrapperStyle={{
                        paddingTop: '20px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Incident List */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900">All Incidents</h3>
            <div className="px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-100 rounded-full">
              {details.incidents.length} incidents found
            </div>
          </div>

          <div className="overflow-hidden border border-gray-200 rounded-xl">
            <div className="overflow-y-auto max-h-96">
              {details.incidents.map((incident: any, index: number) => (
                <div key={incident.id} className={`p-4 border-b border-gray-100 hover:bg-gray-50 ${index === 0 ? '' : ''}`}>
                  <div className="flex items-start justify-between">
                    {/* Incident Number */}
                    <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 mr-4 text-sm font-bold text-white bg-indigo-500 rounded-full">
                      {index + 1}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center mb-2 space-x-3">
                        <h4 className="font-semibold text-gray-900">{incident.title}</h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${incident.severity === 'critical' ? 'bg-red-100 text-red-800' :
                          incident.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                            incident.severity === 'medium' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                          }`}>
                          {incident.severity?.toUpperCase()}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          incident.status === 'reported' ? 'bg-amber-100 text-amber-800' :
                          incident.status === 'investigating' ? 'bg-blue-100 text-blue-800' :
                          incident.status === 'resolved' ? 'bg-green-100 text-green-800' :
                          incident.status === 'dismissed' ? 'bg-red-100 text-red-800' :
                          incident.status === 'uncompliant' ? 'bg-orange-100 text-orange-800' :
                          incident.status === 'sent_for_investigation' ? 'bg-indigo-100 text-indigo-800' :
                          incident.status === 'sent_for_intervention' ? 'bg-purple-100 text-purple-800' :
                          incident.status === 'documented' ? 'bg-teal-100 text-teal-800' :
                          incident.status === 'intervened' ? 'bg-emerald-100 text-emerald-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {incident.status?.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </div>
                      <p className="mb-2 text-sm text-gray-600 line-clamp-2">{incident.description}</p>
                      <div className="grid grid-cols-2 gap-4 text-xs text-gray-500 md:grid-cols-5">
                        {selectedLocation.type === 'district' && (
                          <div>
                            <span className="font-medium">Constituency:</span> {incident.constituency || 'N/A'}
                          </div>
                        )}
                        <div>
                          <span className="font-medium">Polling Center:</span> {incident.center || 'N/A'}
                        </div>
                        <div>
                          <span className="font-medium">Category:</span> {incident.category || 'N/A'}
                        </div>
                        <div>
                          <span className="font-medium">Reported by:</span> {incident.reporter_display_name || 'Unknown'}
                        </div>
                        <div>
                          <span className="font-medium">Reported:</span> {new Date(incident.created_at).toLocaleDateString('en-US', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })} {new Date(incident.created_at).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Location Details */}
        <div className="mt-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Polling Centers */}
            <div className="overflow-hidden border border-gray-200 rounded-xl">
              <div className="px-4 py-3 bg-gray-50">
                <h4 className="font-semibold text-gray-900">Polling Centers ({details.polling_centers.length})</h4>
              </div>
              <div className="p-4 overflow-y-auto max-h-48">
                <div className="grid grid-cols-1 gap-2">
                  {details.polling_centers.map((center: string, index: number) => (
                    <div key={index} className="px-3 py-2 text-sm bg-gray-100 rounded-lg">
                      {center}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Constituencies (only for districts) */}
            {selectedLocation.type === 'district' && details.constituencies.length > 0 && (
              <div className="overflow-hidden border border-gray-200 rounded-xl">
                <div className="px-4 py-3 bg-gray-50">
                  <h4 className="font-semibold text-gray-900">Constituencies ({details.constituencies.length})</h4>
                </div>
                <div className="p-4 overflow-y-auto max-h-48">
                  <div className="grid grid-cols-1 gap-2">
                    {details.constituencies.map((constituency: string, index: number) => (
                      <div key={index} className="px-3 py-2 text-sm bg-gray-100 rounded-lg">
                        {constituency}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ————————————————————————————————————————————————————————————————
// Main Page
// ————————————————————————————————————————————————————————————————

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const goCategory = (cat: string) => navigate(`/admin/incidents/category/${encodeURIComponent(cat)}`);
  // Default map mode for district-level rendering  
  const [mapMode, setMapMode] = React.useState<'district' | 'constituency'>('district');
  // Tracks the MOST RECENT time any of the queries below successfully refreshed
  const [lastUpdatedAt, setLastUpdatedAt] = React.useState<Date | null>(null);
  // Time period selector for temporal analysis
  const [selectedTimePeriod, setSelectedTimePeriod] = React.useState('24h');

  // Location-specific data state
  const [selectedLocation, setSelectedLocation] = React.useState<{ name: string, type: 'district' | 'constituency' } | null>(null);
  const [locationData, setLocationData] = React.useState<any>(null);
  const [locationLoading, setLocationLoading] = React.useState(false);

  const [filters, setFilters] = useState({ district: '', category: '' });

  const { data: lookups } = useQuery('incidents-lookups', () =>
    api.incidents.getAll().then((res: any) => res.data.lookups)
  );

  const { data: categories } = useQuery('incidents-categories', () =>
    api.incidents.getCategories().then((res: any) => res.data.data)
  );

  const handleFilterChange = (filterType: string, value: string) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const markRefreshed = React.useCallback(() => setLastUpdatedAt(new Date()), []);

  // Ensure axios carries token for direct calls (like incidents map)
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  const handleDownload = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams(filters).toString();

    const getFilename = () => {
      const { district, category } = filters;
      const parts = [];
      if (district) parts.push(district.replace(/ /g, '_'));
      if (category) parts.push(category.replace(/ /g, '_'));
      if (parts.length === 0) {
        return 'all_incidents.xlsx';
      }
      return `${parts.join('_')}_incidents.xlsx`;
    };

    axios.get(`/api/incidents/export?${params}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', getFilename());
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    }).catch(error => {
      console.error('Error downloading incidents:', error);
      // You might want to show a toast notification here
    });
  };

  // Shared react-query options to avoid page reloads but keep data fresh
  const baseQueryOpts = {
    refetchInterval: 30_000, // every 30s
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000, // treat data fresh for 1 minute
    cacheTime: 5 * 60_000, // keep cache 5 minutes
    retry: (failureCount: number, error: any) => {
      // Only retry for network errors (no response or 5xx status)
      if (error?.response?.status >= 500 || !error?.response) {
        return failureCount < 3; // Retry up to 3 times
      }
      return false; // Do not retry for other errors (e.g., 4xx)
    },
    retryDelay: (attemptIndex: number) => Math.min(1000 * (2 ** attemptIndex), 30000), // Exponential backoff, max 30s
  } as const;

  // OVERVIEW
  const { data: overview, isLoading: overviewLoading } = useQuery(
    'dashboard-overview',
    () => api.dashboard.getOverview(),
    {
      ...baseQueryOpts,
      onSuccess: markRefreshed,
    }
  );

  // POLLING STATIONS
  const { data: pollingStations, isLoading: pollingStationsLoading } = useQuery(
    'dashboard-polling-stations',
    () => api.dashboard.getPollingStations(),
    {
      ...baseQueryOpts,
      onSuccess: markRefreshed,
    }
  );

  // COMPLIANCE
  const { data: compliance, isLoading: complianceLoading } = useQuery(
    'dashboard-compliance',
    () => api.dashboard.getCompliance(),
    {
      ...baseQueryOpts,
      onSuccess: markRefreshed,
    }
  );

  // PERFORMANCE
  const { data: performance, isLoading: performanceLoading } = useQuery(
    'dashboard-performance',
    () => api.dashboard.getPerformance(),
    {
      ...baseQueryOpts,
      onSuccess: markRefreshed,
    }
  );

  // RECENT ACTIVITY
  const { data: recentActivity, isLoading: activityLoading } = useQuery(
    'recent-activity',
    () => api.dashboard.getRecentActivity({ limit: 10 }),
    {
      ...baseQueryOpts,
      onSuccess: markRefreshed,
    }
  );

  // TIMELINE DATA - Mock data for now
  const timelineData = React.useMemo(() => {
    // Generate mock timeline data based on selected period
    const generateMockData = () => {
      const data = [];
      const now = new Date();
      let periods: number;
      let labelFormat: (date: Date) => string;

      switch (selectedTimePeriod) {
        case '24h':
          periods = 24;
          labelFormat = (date) => date.getHours() + ':00';
          break;
        case '7d':
          periods = 7;
          labelFormat = (date) => date.toLocaleDateString('en-US', { weekday: 'short' });
          break;
        case '30d':
          periods = 30;
          labelFormat = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          break;
        default:
          periods = 12;
          labelFormat = (date) => date.toLocaleDateString('en-US', { month: 'short' });
      }

      for (let i = periods - 1; i >= 0; i--) {
        const date = new Date(now);
        if (selectedTimePeriod === '24h') {
          date.setHours(date.getHours() - i);
        } else if (selectedTimePeriod === '7d') {
          date.setDate(date.getDate() - i);
        } else if (selectedTimePeriod === '30d') {
          date.setDate(date.getDate() - i);
        } else {
          date.setMonth(date.getMonth() - i);
        }

        data.push({
          period: labelFormat(date),
          count: Math.floor(Math.random() * 20) + 1
        });
      }
      return data;
    };

    return generateMockData();
  }, [selectedTimePeriod]);

  // ALERTS (slightly slower refresh cadence)
  const { data: alerts, isLoading: alertsLoading } = useQuery(
    'dashboard-alerts',
    () => api.dashboard.getAlerts(),
    {
      ...baseQueryOpts,
      refetchInterval: 60_000,
      onSuccess: markRefreshed,
    }
  );

  // INCIDENTS MAP (manual axios to your /api/eday/new-incidents-map)
  const {
    data: incidentsMap,
    isLoading: incidentsMapLoading,
    error: incidentsMapError,
  } = useQuery(
    'dashboard-incidents-map',
    async () => {
      const response = await axios.get('/api/eday/new-incidents-map');
      return response.data;
    },
    {
      ...baseQueryOpts,
      onSuccess: markRefreshed,
    }
  );

  // Debug state
  // console.log({ incidentsMap, incidentsMapLoading, incidentsMapError });

  // Handle location click from map
  const handleLocationClick = async (locationName: string, locationType: 'district' | 'constituency') => {
    console.log(`📍 Location clicked: ${locationName} (${locationType})`);

    setSelectedLocation({ name: locationName, type: locationType });
    setLocationLoading(true);

    try {
      const response = await axios.get('/api/eday/incidents-by-location', {
        params: {
          name: locationName,
          type: locationType
        }
      });

      if (response.data.success) {
        setLocationData(response.data.data);
        console.log('📊 Location data fetched successfully:', response.data.data);
      } else {
        console.error('❌ Failed to fetch location data:', response.data.error);
        setLocationData(null);
      }
    } catch (error) {
      console.error('❌ Error fetching location data:', error);
      setLocationData(null);
    } finally {
      setLocationLoading(false);
    }
  };

  // Handle closing location view
  const handleCloseLocationView = () => {
    setSelectedLocation(null);
    setLocationData(null);
  };

  if (
    overviewLoading ||
    pollingStationsLoading ||
    complianceLoading ||
    performanceLoading
  ) {
    return <LoadingSpinner fullScreen />;
  }

  // Unwrap
  const stats = overview?.data?.data;
  const pollingData = pollingStations?.data?.data;
  const complianceData = compliance?.data?.data;
  const performanceData = performance?.data?.data;

  console.log('compliance Data:', complianceData);



  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      <div className="container px-4 py-8 mx-auto space-y-8 sm:px-6 lg:px-8">
        {/* Enhanced Header Section */}
        <div className="p-6 border border-gray-200 shadow-xl xl:p-8 bg-gradient-to-r from-white via-red-50 to-pink-50 rounded-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-transparent xl:text-4xl bg-gradient-to-r from-gray-900 via-red-900 to-pink-900 bg-clip-text">
                Incidents Command Center
              </h1>
              <p className="mt-2 text-gray-600 xl:text-lg">Real-time incident monitoring and comprehensive analysis dashboard</p>
            </div>
          
          </div>
          {/* Download Section */}
          <div className="mt-6">
            <div className="flex items-center space-x-4">
              <h3 className="text-lg font-semibold text-gray-800">Download Incidents</h3>
              <select
                className="w-64 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                value={filters.district}
                onChange={(e) => handleFilterChange('district', e.target.value)}
              >
                <option value="">All Districts</option>
                {lookups?.districts?.map((d: any) => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
              <select
                className="w-48 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">All Categories</option>
                {categories?.map((c: any) => <option key={c.code} value={c.name}>{c.name}</option>)}
              </select>
              <button
                onClick={handleDownload}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ArrowDownTrayIcon className="w-5 h-5 mr-2 -ml-1" />
                Download
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Key Metrics - Modern Grid Layout */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {/* Security Reporting - Enhanced */}
          <button onClick={() => goCategory('Security')} className="relative p-6 overflow-hidden text-left transition-all duration-300 border border-green-200 group bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 rounded-2xl hover:shadow-xl">
            <div className="absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6 bg-green-500 rounded-full opacity-10"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-green-100 rounded-xl">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              </div>
              <div className="mb-1 text-2xl font-bold text-green-700 xl:text-3xl">
                {incidentsMap?.category_counts?.['Security']?.toLocaleString() || 0}
              </div>
              <div className="text-sm font-medium text-green-600 xl:text-base">Security Reporting</div>
            </div>
          </button>

          {/* Health and Environment - Enhanced */}
          <button onClick={() => goCategory('Health/Environment')} className="relative p-6 overflow-hidden text-left transition-all duration-300 border border-blue-200 group bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 rounded-2xl hover:shadow-xl">
            <div className="absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6 bg-blue-500 rounded-full opacity-10"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              </div>
              <div className="mb-1 text-2xl font-bold text-blue-700 xl:text-3xl">
                {incidentsMap?.category_counts?.['Health/Environment']?.toLocaleString() || 0}
              </div>
              <div className="text-sm font-medium text-blue-600 xl:text-base">Health & Environment</div>
            </div>
          </button>

          {/* Gender - Enhanced */}
          <button onClick={() => goCategory('Gender')} className="relative p-6 overflow-hidden text-left transition-all duration-300 border border-red-200 group bg-gradient-to-br from-red-50 via-pink-50 to-red-100 rounded-2xl hover:shadow-xl">
            <div className="absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6 bg-red-500 rounded-full opacity-10"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-red-100 rounded-xl">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="relative">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
                  <div className="absolute inset-0 w-2 h-2 bg-red-400 rounded-full"></div>
                </div>
              </div>
              <div className="mb-1 text-2xl font-bold text-red-700 xl:text-3xl">
                {incidentsMap?.category_counts?.['Gender']?.toLocaleString() || 0}
              </div>
              <div className="text-sm font-medium text-red-600 xl:text-base">Gender Issues</div>
            </div>
          </button>

          {/* Campaign/Elections - Enhanced */}
          <button onClick={() => goCategory('Campaign/Elections')} className="relative p-6 overflow-hidden text-left transition-all duration-300 border border-purple-200 group bg-gradient-to-br from-purple-50 via-violet-50 to-purple-100 rounded-2xl hover:shadow-xl">
            <div className="absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6 bg-purple-500 rounded-full opacity-10"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-purple-100 rounded-xl">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
              </div>
              <div className="mb-1 text-2xl font-bold text-purple-700 xl:text-3xl">
                {incidentsMap?.category_counts?.['Campaign/Elections']?.toLocaleString() || 0}
              </div>
              <div className="text-sm font-medium text-purple-600 xl:text-base">Campaign / Elections</div>
            </div>
          </button>

          {/* Governance and Human Rights - Enhanced */}
          <button onClick={() => goCategory('Governance and human rights violations')} className="relative p-6 overflow-hidden text-left transition-all duration-300 border border-yellow-200 group bg-gradient-to-br from-yellow-50 via-amber-50 to-yellow-100 rounded-2xl hover:shadow-xl">
            <div className="absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6 bg-yellow-500 rounded-full opacity-10"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-yellow-100 rounded-xl">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                </div>
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
              </div>
              <div className="mb-1 text-2xl font-bold text-yellow-700 xl:text-3xl">
                {incidentsMap?.category_counts?.['Governance and human rights violations']?.toLocaleString() || 0}
              </div>
              <div className="text-sm font-medium text-yellow-600 xl:text-base">Governance & Rights</div>
            </div>
          </button>

          {/* Media and Information - Enhanced */}
          <button onClick={() => goCategory('Media and Information')} className="relative p-6 overflow-hidden text-left transition-all duration-300 border border-indigo-200 group bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-100 rounded-2xl hover:shadow-xl">
            <div className="absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6 bg-indigo-500 rounded-full opacity-10"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-indigo-100 rounded-xl">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                </div>
                <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
              </div>
              <div className="mb-1 text-2xl font-bold text-indigo-700 xl:text-3xl">
                {incidentsMap?.category_counts?.['Media and Information']?.toLocaleString() || 0}
              </div>
              <div className="text-sm font-medium text-indigo-600 xl:text-base">Media & Information</div>
            </div>
          </button>

          {/* Children Under 18 - Placeholder */}
          <div className="relative p-6 overflow-hidden text-left transition-all duration-300 border border-orange-200 group bg-gradient-to-br from-orange-50 via-yellow-50 to-orange-100 rounded-2xl hover:shadow-xl">
            <div className="absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6 bg-orange-500 rounded-full opacity-10"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-orange-100 rounded-xl">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-6-3a9 9 0 1118 0M10 10a3 3 0 11-6 0 3 3 0 016 0zm-2 8a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
              </div>
              <div className="mb-1 text-2xl font-bold text-orange-700 xl:text-3xl">
                {/* Data for children under 18 is not directly available from current API. */}
                {/* Please ensure the backend provides this count or an age field in incident data. */}
                N/A
              </div>
              <div className="text-sm font-medium text-orange-600 xl:text-base">Children Under 18</div>
            </div>
          </div>
        </div>

        {/* Enhanced Main Content Section */}
        <div className="grid grid-cols-1 gap-8 xl:grid-cols-5">
          {/* Enhanced Incidents Map Section */}
          <div className="xl:col-span-2">
            <div className="overflow-hidden bg-white border border-gray-200 shadow-xl rounded-2xl">
              <div className="relative px-6 py-4 bg-gradient-to-r from-red-500 via-pink-500 to-rose-500">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-white">Live Incidents Map</h3>
                    <div className="flex items-center space-x-3">
                      <div className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm">
                        <span className="text-sm font-medium text-white">Total:</span>
                      </div>
                      <div className="text-2xl font-bold text-white">
                        {(() => {
                          // Debug: Log the calculation
                          const total = (() => {
                            // Calculate total from category_counts if available
                            if (incidentsMap?.category_counts) {
                              return Object.values(incidentsMap.category_counts).reduce((sum: number, count: any) => sum + (count || 0), 0);
                            }
                            // Fallback to nested data structure
                            if (incidentsMap?.data?.map_incidents?.total_incidents) {
                              return incidentsMap.data.map_incidents.total_incidents;
                            }
                            // Final fallback
                            return stats?.incidents?.total_incidents || 0;
                          })();

                          console.log('🔢 Dashboard total incidents calculated:', total);
                          return total.toLocaleString();
                        })()}
                      </div>
                      <div className="relative">
                        <div className="w-3 h-3 bg-white rounded-full animate-ping"></div>
                        <div className="absolute inset-0 w-3 h-3 rounded-full bg-white/80"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="relative overflow-hidden bg-gray-50 rounded-xl">
                  <MalawiDistrictsMap
                    incidentsData={incidentsMap}
                    loading={incidentsMapLoading}
                    height={520}
                    onLocationClick={handleLocationClick}
                  />
                  {incidentsMapLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm">
                      <div className="text-center">
                        <div className="w-8 h-8 mx-auto mb-2 border-4 border-red-200 rounded-full border-t-red-500 animate-spin"></div>
                        <p className="text-sm font-medium text-gray-600">Loading incidents data...</p>
                      </div>
                    </div>
                  )}
                  {Boolean(incidentsMapError) && (
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="p-3 border border-red-200 rounded-lg bg-red-50">
                        <p className="text-sm font-medium text-red-600">⚠️ Failed to load incidents map data</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Statistics Section - Conditional rendering for location-specific data */}
          <div className="xl:col-span-3">
            {selectedLocation ? (
              <LocationIncidentsView
                locationData={locationData}
                selectedLocation={selectedLocation}
                loading={locationLoading}
                onClose={handleCloseLocationView}
              />
            ) : (
              <div className="overflow-hidden bg-white border border-gray-200 shadow-xl rounded-2xl">
                <div className="relative px-6 py-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">
                  <div className="absolute inset-0 bg-black/10"></div>
                  <div className="relative z-10">
                    <h2 className="flex items-center text-xl font-bold text-white">
                      <div className="p-2 mr-3 bg-white/20 backdrop-blur-sm rounded-xl">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2-2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      Incidents Analytics
                      <div className="flex items-center ml-4 space-x-2">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-white/90">LIVE</span>
                      </div>
                    </h2>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                    {/* Enhanced Resolution Status */}
                    <div className="space-y-6">
                      {/* Resolution Overview Card */}
                      <div className="relative p-6 overflow-hidden transition-all duration-300 border-2 border-green-200 bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 rounded-2xl hover:shadow-xl">
                        <div className="absolute top-0 right-0 w-24 h-24 transform translate-x-8 -translate-y-8 bg-green-500 rounded-full opacity-10"></div>
                        <div className="relative z-10 text-center">
                          <h3 className="mb-4 text-lg font-bold text-gray-900">Resolution Progress</h3>
                          <div className="mb-2 text-4xl font-bold text-green-700">
                            {(() => {
                              const sc = incidentsMap?.status_counts || {};
                              const total = Object.values(sc).reduce((acc: number, val) => acc + (val as number), 0);
                              const resolved = sc.resolved || 0;
                              return total > 0 ? ((resolved / total) * 100).toFixed(1) : '0.0';
                            })()}%
                          </div>
                          <div className="text-sm font-medium text-green-600">
                            {(() => {
                              const resolved = incidentsMap?.status_counts?.resolved || 0;
                              const total = Object.values(incidentsMap?.status_counts || {}).reduce((acc: number, val) => acc + (val as number), 0);
                              return `${resolved.toLocaleString()} of ${(total as number).toLocaleString()} resolved`;
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Security Level Breakdown */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-bold text-gray-900">Security Levels</h3>
                          <div className="px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-100 rounded-full">
                            Priority Analysis
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="relative p-4 overflow-hidden transition-all duration-300 border-2 border-red-200 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl hover:shadow-lg">
                            <div className="absolute top-0 right-0 w-12 h-12 transform translate-x-2 -translate-y-2 bg-red-500 rounded-full opacity-10"></div>
                            <div className="relative z-10 text-center">
                              <div className="mb-1 text-2xl font-bold text-red-700">
                                {incidentsMap?.severity_counts?.critical || 0}
                              </div>
                              <div className="text-sm font-medium text-red-600">Critical</div>
                            </div>
                          </div>
                          <div className="relative p-4 overflow-hidden transition-all duration-300 border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl hover:shadow-lg">
                            <div className="absolute top-0 right-0 w-12 h-12 transform translate-x-2 -translate-y-2 bg-orange-500 rounded-full opacity-10"></div>
                            <div className="relative z-10 text-center">
                              <div className="mb-1 text-2xl font-bold text-orange-700">
                                {incidentsMap?.severity_counts?.high || 0}
                              </div>
                              <div className="text-sm font-medium text-orange-600">High Priority</div>
                            </div>
                          </div>
                          <div className="relative p-4 overflow-hidden transition-all duration-300 border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl hover:shadow-lg">
                            <div className="absolute top-0 right-0 w-12 h-12 transform translate-x-2 -translate-y-2 bg-blue-500 rounded-full opacity-10"></div>
                            <div className="relative z-10 text-center">
                              <div className="mb-1 text-2xl font-bold text-blue-700">
                                {incidentsMap?.severity_counts?.medium || 0}
                              </div>
                              <div className="text-sm font-medium text-blue-600">Medium Priority</div>
                            </div>
                          </div>
                          <div className="relative p-4 overflow-hidden transition-all duration-300 border-2 border-gray-200 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl hover:shadow-lg">
                            <div className="absolute top-0 right-0 w-12 h-12 transform translate-x-2 -translate-y-2 bg-gray-500 rounded-full opacity-10"></div>
                            <div className="relative z-10 text-center">
                              <div className="mb-1 text-2xl font-bold text-gray-700">
                                {incidentsMap?.severity_counts?.low || 0}
                              </div>
                              <div className="text-sm font-medium text-gray-600">Low Priority</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Enhanced Status Chart */}
                    <div className="space-y-6">
                      <div className="relative p-6 overflow-hidden transition-all duration-300 border-2 border-gray-200 bg-gradient-to-br from-white via-gray-50 to-white rounded-2xl hover:shadow-xl">
                        <div className="absolute top-0 right-0 w-24 h-24 transform translate-x-8 -translate-y-8 bg-indigo-500 rounded-full opacity-5"></div>
                        <div className="relative z-10">
                          <h3 className="mb-6 text-lg font-bold text-center text-gray-900">Status Distribution (Excluding Investigating)</h3>
                          <ResponsiveContainer width="100%" height={320}>
                            <PieChart>
                              <Pie
                                data={[
                                  { name: 'Reported', value: incidentsMap?.status_counts?.reported || 0 },
                                  { name: 'Resolved', value: incidentsMap?.status_counts?.resolved || 0 },
                                  { name: 'Dismissed', value: incidentsMap?.status_counts?.dismissed || 0 },
                                  { name: 'Uncompliant', value: incidentsMap?.status_counts?.uncompliant || 0 },
                                  { name: 'Sent for Investigation', value: incidentsMap?.status_counts?.sent_for_investigation || 0 },
                                  { name: 'Sent for Intervention', value: incidentsMap?.status_counts?.sent_for_intervention || 0 },
                                  { name: 'Documented', value: incidentsMap?.status_counts?.documented || 0 },
                                  { name: 'Intervened', value: incidentsMap?.status_counts?.intervened || 0 }
                                ].filter(item => item.value > 0)} // Only show statuses with incidents
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={90}
                                innerRadius={30}
                                paddingAngle={2}
                              >
                                <Cell fill="#F59E0B" /> {/* Reported - amber */}
                                <Cell fill="#10B981" /> {/* Resolved - green */}
                                <Cell fill="#EF4444" /> {/* Dismissed - red */}
                                <Cell fill="#F97316" /> {/* Uncompliant - orange */}
                                <Cell fill="#3B82F6" /> {/* Sent for Investigation - blue */}
                                <Cell fill="#8B5CF6" /> {/* Sent for Intervention - purple */}
                                <Cell fill="#14B8A6" /> {/* Documented - teal */}
                                <Cell fill="#059669" /> {/* Intervened - emerald */}
                              </Pie>
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '12px',
                                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
                                }}
                              />
                              <Legend
                                verticalAlign="bottom"
                                height={36}
                                wrapperStyle={{
                                  paddingTop: '20px'
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
